import { memo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  useCue,
  useCueTimestamp,
  usePreviousCueId,
} from '@/features/rundown/cue/cue.hooks'

import { useTimingInfo } from './timing.hooks'
import { useTimingFixPlanner } from './timingFixes.hooks'
import { TimingFixMenu } from './TimingFixMenu'
import type { TimingFixAction } from './timingFixTypes'

type TimingIndicatorProps = {
  cueId: string
  timezone?: string
  // Runs a fix's write-intent. Always provided by the row; `readonly` hides the
  // Fix menu in views without edit access.
  onRunFixAction: (action: TimingFixAction) => void
  readonly?: boolean
}

export const TimingIndicator = memo(function TimingIndicator({
  cueId,
  timezone = 'UTC',
  onRunFixAction,
  readonly = false,
}: TimingIndicatorProps) {
  const cue = useCue(cueId)
  const previousCueId = usePreviousCueId(cueId)
  const timestamp = useCueTimestamp(cueId)
  const previousCueTimestamp = useCueTimestamp(previousCueId)

  const timingInfo = useTimingInfo(
    cue,
    previousCueTimestamp,
    timestamp,
    timezone,
  )

  const buildFixes = useTimingFixPlanner(cueId, onRunFixAction)

  if (!timestamp || !previousCueTimestamp) return null

  const {
    type,
    text,
    textColor = '',
    bgColor = '',
    icon,
    differenceMs,
  } = timingInfo

  // A gap/overlap is the only fixable state (type is set only then). The
  // descriptors are NOT built here: `buildFixes` plans every strategy, which
  // runs createTimestamps plus one planBacktime per cue above the anchor. Doing
  // that in the render body repeats it on every tick for every gap cue. Pass
  // the thunk down instead, so TimingFixMenu calls it when the menu opens.
  const fixType = type === 'Gap' || type === 'Overlap' ? type : null
  const isFixable = fixType !== null && !readonly

  return (
    <div className="flex items-center gap-2 leading-5 text-xs text-gray-500">
      <span className={[textColor, 'relative flex h-3 w-3'].join(' ')}>
        <span
          className={[
            'animate-ping absolute inline-flex h-full w-full rounded-full opacity-50',
            bgColor,
          ].join(' ')}
        ></span>
        {icon && <FontAwesomeIcon icon={icon} />}
      </span>
      <span>{text}</span>
      {isFixable ? (
        <TimingFixMenu
          type={fixType}
          buildFixes={() =>
            buildFixes({ differenceMs: differenceMs ?? 0, type: fixType })
          }
        />
      ) : null}
    </div>
  )
})
